// DDL constraint / reference slot recognizers for the EditorContext projection.
// PRD: GRA-8 (MSSQL projection pivot), design §2.2. G11: pure text scan,
// NO web-tree-sitter — this is the legal projection-side body the future
// tree-sitter swap replaces behind the identical signature.
//
// GRA8-ROLL-1 extracted this shared DDL-constraint family out of
// editorContextSlot.ts. These detectors key on standard cross-engine DDL
// keywords (REFERENCES / CHECK / PRIMARY KEY / FOREIGN KEY / UNIQUE), so they
// stay reusable by later cross-engine slices.
//
// CREATE/ALTER TABLE constraint bodies collapse to `other` → `unknown` in the
// text clause machine, so mssql (which — unlike the other 4 resolvers — never
// called the since-retired string-walk `classifyDdlPositionKind`) surfaced
// stray keywords there. These token-level detectors name the precise slot so
// the resolver can dispatch the same cross-engine emission. They originally
// mirrored the string-walk's regex shapes in ClauseToken form; plan19 T7 then
// ported the FULL nine-kind family into the sibling `editorContextSlot.ddl2.ts`
// (`classifyDdlSlot`) and deleted the string-walk — this narrower mssql-only
// subset stays as the pilot's dispatch. Pinned by mssql script.29 §5/§6 and
// script.30 §3/§4/§6.

import type { EditorContextSlotKind } from '../model/editorContext';
import type { ClauseToken } from './editorContextClause';
import { findEnclosingLparenIndex, upper } from './editorContextSlotShared';

export type DdlConstraintSlotKind = Extract<
  EditorContextSlotKind,
  | 'ddl-references-column-list'
  | 'ddl-create-table-expression'
  | 'ddl-alter-table-constraint-column-list'
  | 'ddl-fk-target'
>;

export function ddlConstraintSlot(
  tokens: readonly ClauseToken[],
): DdlConstraintSlotKind | null {
  const prev = tokens[tokens.length - 1];
  if (!prev) return null;

  const lparenIdx = findEnclosingLparenIndex(tokens);
  if (lparenIdx >= 0) {
    // Cursor sits inside an unclosed `(` — classify the column-list slot by the
    // tokens immediately preceding the opener.
    // 1. REFERENCES <tbl>(▮  → the REFERENCED table's column list.
    if (precededByReferencesTarget(tokens, lparenIdx)) {
      return 'ddl-references-column-list';
    }
    // 2. CHECK / PRIMARY KEY / UNIQUE / FOREIGN KEY (▮  inside a CREATE or
    //    ALTER TABLE constraint clause.
    const constraintLead = constraintLeadKind(tokens, lparenIdx);
    if (constraintLead !== null) {
      const head = headTableVerb(tokens);
      if (head === 'create') return 'ddl-create-table-expression';
      // ALTER TABLE accepts only PK / FK / UNIQUE column lists here; CHECK /
      // GENERATED inside ALTER TABLE are rarer and intentionally skipped
      // (matching the retired string-walk classifier and `classifyDdlSlot`).
      if (head === 'alter' && constraintLead === 'key-or-unique') {
        return 'ddl-alter-table-constraint-column-list';
      }
    }
    return null;
  }

  // Cursor is NOT inside an unclosed `(`.
  // REFERENCES ▮ (with an optional partial target identifier already typed) is
  // the FK-target table-name slot.
  if (followsReferencesTableSlot(tokens)) return 'ddl-fk-target';

  return null;
}

/**
 * True when the unclosed `(` at `lparenIdx` is immediately preceded by
 * `REFERENCES <table>` (bare or schema-qualified, including bracket-quoted
 * parts). The slot is the referenced table's column list.
 */
function precededByReferencesTarget(
  tokens: readonly ClauseToken[],
  lparenIdx: number,
): boolean {
  // Walk back over the dotted table name: `<ident> ( . <ident> )?` ending at
  // the token just before the `(`.
  let i = lparenIdx - 1;
  if (i < 0 || tokens[i]!.kind !== 'identifier') return false;
  i -= 1;
  if (i >= 0 && tokens[i]!.kind === 'dot') {
    i -= 1;
    if (i < 0 || tokens[i]!.kind !== 'identifier') return false;
    i -= 1;
  }
  return upper(tokens[i]) === 'REFERENCES';
}

/**
 * Classify the constraint keyword immediately preceding the unclosed `(` at
 * `lparenIdx`. Returns `'check'` for CHECK, `'key-or-unique'` for PRIMARY KEY /
 * FOREIGN KEY / UNIQUE, or `null` when the opener is not a constraint clause.
 */
function constraintLeadKind(
  tokens: readonly ClauseToken[],
  lparenIdx: number,
): 'check' | 'key-or-unique' | null {
  const last = upper(tokens[lparenIdx - 1]);
  if (last === 'CHECK') return 'check';
  if (last === 'UNIQUE') return 'key-or-unique';
  // PRIMARY KEY / FOREIGN KEY — the opener is preceded by `KEY` whose prior
  // token is PRIMARY or FOREIGN.
  if (last === 'KEY') {
    const before = upper(tokens[lparenIdx - 2]);
    if (before === 'PRIMARY' || before === 'FOREIGN') return 'key-or-unique';
  }
  return null;
}

/**
 * Identify the enclosing-statement table verb: `'create'` for a CREATE TABLE,
 * `'alter'` for an ALTER TABLE, or `null` otherwise. Scans the head tokens for
 * a `CREATE … TABLE` / `ALTER TABLE` lead, returning the verb of the LAST such
 * lead seen before the cursor (closest enclosing statement). Exported so the
 * per-engine recognizers (GRA8-ROLL-2, mysql) can re-use the same head scan.
 */
export function headTableVerb(tokens: readonly ClauseToken[]): 'create' | 'alter' | null {
  let verb: 'create' | 'alter' | null = null;
  for (let i = 0; i < tokens.length; i += 1) {
    const u = upper(tokens[i]);
    if (u === 'CREATE' && nextNonNoiseIsTable(tokens, i + 1)) verb = 'create';
    else if (u === 'ALTER' && upper(tokens[i + 1]) === 'TABLE') verb = 'alter';
  }
  return verb;
}

/**
 * True when the next meaningful keyword/identifier after index `from` is
 * `TABLE`, tolerating an intervening `TEMP`/`TEMPORARY`/`OR`/`REPLACE` (so
 * `CREATE TABLE`, `CREATE TEMP TABLE`, etc. all match). Stops at the first
 * non-keyword identifier (the table name) — used to confirm a CREATE … TABLE
 * head, not a CREATE INDEX / VIEW.
 */
function nextNonNoiseIsTable(tokens: readonly ClauseToken[], from: number): boolean {
  const NOISE = new Set(['TEMP', 'TEMPORARY', 'OR', 'REPLACE', 'GLOBAL', 'LOCAL']);
  for (let i = from; i < tokens.length; i += 1) {
    const u = upper(tokens[i]);
    if (u === null) return false;
    if (u === 'TABLE') return true;
    if (NOISE.has(u)) continue;
    return false;
  }
  return false;
}

/**
 * True when the cursor follows a `REFERENCES` table-name slot: the most recent
 * `REFERENCES` keyword precedes the cursor with at most a partial dotted target
 * identifier typed (`REFERENCES `, `REFERENCES Sales`, `REFERENCES Sales.`),
 * and the cursor is NOT yet inside the referenced table's column-list `(`
 * (that is `ddl-references-column-list`, handled above). We accept only the
 * `<ident> ( . <ident>? )?` tail so a fully-formed `REFERENCES tbl col` (two
 * separate idents) does not match.
 */
function followsReferencesTableSlot(tokens: readonly ClauseToken[]): boolean {
  // Bare slot: cursor right after `REFERENCES ` with no target typed yet.
  if (upper(tokens[tokens.length - 1]) === 'REFERENCES') return true;

  let i = tokens.length - 1;
  // Optional trailing partial identifier (`REFERENCES Sales` / the second part
  // of `Sales.`).
  if (i >= 0 && tokens[i]!.kind === 'identifier') i -= 1;
  // Optional dot for a schema-qualified target (`REFERENCES Sales.`).
  if (i >= 0 && tokens[i]!.kind === 'dot') {
    i -= 1;
    if (i >= 0 && tokens[i]!.kind === 'identifier') i -= 1;
  }
  return upper(tokens[i]) === 'REFERENCES';
}
